import React, { PureComponent } from "react";
import styled from "styled-components";
import { getRequest, postRequest } from "../../api/ApiService";
import Auth from "../../routes/Auth";
import Teams from "../../api/Teams";
import SkillsBadgePopup from "../skills-badge/SkillsBadgePopup";

// Fetches the student's badge on mount from /badges/mine/. If a
// pending badge exists (not consented, not revoked, not declined),
// shows a three-step flow:
//   1) Congratulations intro popup — Continue only
//   2) Tour popup with the student_message body (URL stripped,
//      "Here's your badge:" replaced with a "View your badge" link)
//      + a View Badge button
//   3) Skills Badge design popup — the shared SkillsBadgePopup
//      driven by /v2/insights/mine/<surveyId>/, with Decline / Accept
//      action buttons in the footer
//
// No dismiss affordance anywhere in the flow: the student must
// commit to Accept or Decline. Until they do, the badge stays
// pending on the BE and the flow re-appears on every login.
// Accept → POST /badges/consent/<token>/ (publishes badge).
// Decline → POST /badges/decline/<token>/ (opts out).

/* Shared backdrop — dims everything below the topbar. */
const Backdrop = styled.div`
  position: fixed;
  top: 59px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 27, 59, 0.55);
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

/* Intro popup */

const IntroCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 520px;
  padding: 32px 36px;
  text-align: center;
`;

const IntroTitle = styled.h2`
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 22px;
  line-height: 30px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0 0 24px;
`;

const IntroActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const PrimaryButton = styled.button`
  background: #2DA548;
  border: 1px solid #2DA548;
  border-radius: 4px;
  padding: 10px 28px;
  font-family: 'Mukta Vaani';
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #ffffff;
  cursor: pointer;

  &:hover {
    background: #248339;
    border-color: #248339;
  }

  &:disabled {
    background: #a4d9b1;
    border-color: #a4d9b1;
    cursor: default;
  }
`;

const SecondaryButton = styled.button`
  background: #ffffff;
  border: 1px solid #D5D9DD;
  border-radius: 4px;
  padding: 10px 20px;
  font-family: 'Mukta Vaani';
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: rgba(0, 27, 59, 0.94);
  cursor: pointer;

  &:hover {
    background: #f5f6f7;
  }

  &:disabled {
    cursor: default;
    color: rgba(0, 27, 59, 0.4);
  }
`;

const DeclineButton = styled(SecondaryButton)``;

/* Confirmation card — shown after Accept succeeds so the student
   gets an in-app receipt with their public badge URL. Also mentions
   the confirmation email so they know the same link is in their
   inbox. */

const ConfirmationCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 560px;
  padding: 36px 40px;
  font-family: 'Mukta Vaani', sans-serif;
  text-align: center;
`;

const ConfirmationCheckmark = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #2DA548;
  color: #ffffff;
  font-size: 34px;
  line-height: 64px;
  margin: 0 auto 20px;
`;

const ConfirmationTitle = styled.h2`
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0 0 12px;
`;

const ConfirmationDescription = styled.p`
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.78);
  margin: 0 0 24px;
`;

const UrlLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 27, 59, 0.78);
  text-align: left;
  margin-bottom: 8px;
`;

const UrlRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 28px;
`;

/* Read-only text field the student can select to copy the URL
   manually. Also styled to feel like a familiar "copy this" field. */
const UrlField = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  padding: 10px 14px;
  background: #F1F4F8;
  border: 1px solid #D5D9DD;
  border-radius: 6px;
  font-family: 'Mukta Vaani', sans-serif;
  font-size: 15px;
  color: rgba(0, 27, 59, 0.94);
`;

const CopyButton = styled.button`
  flex: 0 0 auto;
  padding: 10px 16px;
  background: #ffffff;
  border: 1px solid #D5D9DD;
  border-radius: 6px;
  font-family: 'Mukta Vaani', sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: rgba(0, 27, 59, 0.94);
  cursor: pointer;

  &:hover { background: #f5f6f7; }
`;

const ConfirmationActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
`;

const ViewButton = styled.a`
  padding: 10px 28px;
  background: #2DA548;
  border: 1px solid #2DA548;
  border-radius: 4px;
  font-family: 'Mukta Vaani', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #ffffff;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background: #248339;
    border-color: #248339;
  }
`;

/* Tour popup */

const TourCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
  width: 100%;
  max-width: 620px;
  max-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TourHeader = styled.div`
  padding: 24px 32px 12px;
  border-bottom: 1px solid #ECEDEF;
`;

const TourTitle = styled.h2`
  font-family: 'Mukta Vaani';
  font-weight: 700;
  font-size: 22px;
  line-height: 30px;
  color: rgba(0, 27, 59, 0.94);
  margin: 0;
`;

const TourBody = styled.div`
  padding: 20px 32px;
  overflow-y: auto;
  flex: 1 1 auto;
  font-family: 'Mukta Vaani';
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 27, 59, 0.94);
`;

/* Paragraph inside the tour body. Preserves any single \n inside a
   paragraph (e.g. the signature) via pre-wrap. Gap between paragraphs
   is handled with margin-bottom instead of the raw \n\n. */
const TourParagraph = styled.div`
  margin-bottom: 16px;
  white-space: pre-wrap;

  &:last-child {
    margin-bottom: 0;
  }
`;

/* Highlighted resume-wording block — light gray card so the copy the
   student can paste into LinkedIn / Handshake / résumé visually stands
   out from the surrounding narrative. */
const TourHighlightBox = styled.div`
  margin: 4px 0 16px;
  padding: 16px 20px;
  background: #F1F4F8;
  border-left: 3px solid #F4A93C;
  border-radius: 8px;
  white-space: pre-wrap;
`;

/* Inline external link (LinkedIn / Handshake). Kept subtle so it
   doesn't compete with the primary "View your badge" affordance. */
const TourExternalLink = styled.a`
  color: #1f6feb;
  text-decoration: underline;

  &:hover {
    color: #0d5bc0;
  }
`;

const TourFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #ECEDEF;
  background: #ffffff;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  align-items: center;
`;

const ErrorMsg = styled.div`
  font-family: 'Mukta Vaani';
  font-size: 14px;
  color: #d34c4c;
  margin-right: auto;
`;

/* Wraps the Accept button so we can render a custom hover tooltip
   explaining what accepting the badge unlocks. Native `title` would
   render at the browser's default (< 16px) size — see the project's
   hover-text convention. Custom CSS tooltip lets us honor the ≥16px
   rule and gives us nicer styling. */
const AcceptButtonWrap = styled.span`
  position: relative;
  display: inline-block;

  .accept-tooltip {
    position: absolute;
    bottom: calc(100% + 10px);
    right: 0;
    width: 300px;
    padding: 12px 16px;
    background: rgba(0, 27, 59, 0.94);
    color: #ffffff;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
    font-family: 'Mukta Vaani', sans-serif;
    font-size: 16px;
    line-height: 22px;
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.15s ease;
    pointer-events: none;
    z-index: 20;
  }

  /* Small arrow pointing down at the Accept button. */
  .accept-tooltip::after {
    content: "";
    position: absolute;
    top: 100%;
    right: 32px;
    border: 6px solid transparent;
    border-top-color: rgba(0, 27, 59, 0.94);
  }

  &:hover .accept-tooltip,
  &:focus-within .accept-tooltip {
    visibility: visible;
    opacity: 1;
  }
`;

/* Inline "View your badge" link inside the tour body — styled as a
   text link but implemented as a button for accessibility. */
const ViewBadgeLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: #1f6feb;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: #0d5bc0;
  }
`;


class StudentBadgesFlow extends PureComponent {
  state = {
    badge: null,
    checked: false,
    tourOpen: false,
    designOpen: false,
    submitting: false,
    error: null,
    // Insights payload for the shared badge popup — fetched lazily
    // the first time the design step opens.
    insightsData: null,
    insightsLoading: false,
    insightsError: null,
    // Populated after a successful Accept — holds the public_url and
    // manage_url from the consent response so we can show them on the
    // in-app confirmation screen.
    consentResult: null,
    copied: false,
  };

  componentDidMount() {
    this.fetchBadge();
  }

  componentWillUnmount() {
    if (this._copyTimeout) clearTimeout(this._copyTimeout);
  }

  fetchBadge = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    getRequest(`${apiUrl}v2/badges/mine/`)
      .then(res => {
        const body = (res && res.body) || {};
        const badges = Array.isArray(body.badges) ? body.badges : [];
        // Per the guide: 1 badge per student per course. Popup
        // appears when the student hasn't consented AND hasn't
        // declined — those are the two terminal states we skip.
        const pending = badges.find(
          b => !b.is_consented && !b.is_declined
        );
        this.setState({ badge: pending || null, checked: true });
      })
      .catch(() => {
        // Endpoint 404s / errors — no popup rather than a broken
        // affordance. Student can still see badges elsewhere later.
        this.setState({ badge: null, checked: true });
      });
  };

  openTour = () => {
    this.setState({ tourOpen: true });
  };

  openDesign = () => {
    this.setState({ designOpen: true, error: null });
    if (!this.state.insightsData && !this.state.insightsLoading) {
      this.fetchInsights();
    }
  };

  // Direct fetch: badge.survey_id is authoritative for which survey's
  // peer-feedback insights back this badge. Silently degrades if the
  // call fails — the shared popup renders the snapshot summary text.
  fetchInsights = () => {
    const { badge } = this.state;
    const surveyId = badge && badge.survey_id;
    if (!surveyId) {
      this.setState({ insightsError: "No survey linked to this badge" });
      return;
    }
    const apiUrl = process.env.REACT_APP_API_URL;
    this.setState({ insightsLoading: true, insightsError: null });
    getRequest(`${apiUrl}v2/insights/mine/${surveyId}/`)
      .then(res => {
        this.setState({
          insightsData: (res && res.body) || null,
          insightsLoading: false,
        });
      })
      .catch(() => {
        this.setState({
          insightsLoading: false,
          insightsError: "Unable to load badge",
        });
      });
  };

  // Resolve the current student's display name from the cached user
  // object populated by /users/<id>/get_info/. Falls back to empty
  // string if the user isn't populated yet.
  getStudentName = () => {
    const user = Auth.getUser();
    if (!user) return "";
    const first = user.first_name || "";
    const last = user.last_name || "";
    return `${first} ${last}`.trim();
  };

  // Teammate count = current team's players minus self. Used as the
  // radar's "% of teammates who commented on this skill" denominator.
  getTeammateCount = () => {
    const players = Teams.players() || [];
    if (players.length === 0) return 0;
    const myUserId = Number(Auth.getUserId());
    return players.filter(p => p && p.user && Number(p.user.id) !== myUserId).length;
  };

  approve = () => {
    const { badge, submitting } = this.state;
    if (!badge || submitting) return;
    this.setState({ submitting: true, error: null });
    const apiUrl = process.env.REACT_APP_API_URL;
    postRequest(`${apiUrl}v2/badges/consent/${badge.consent_token}/`)
      .send({})
      .then(res => {
        const body = (res && res.body) || {};
        this.setState({
          submitting: false,
          tourOpen: false,
          designOpen: false,
          badge: Object.assign({}, badge, { is_consented: true }),
          consentResult: {
            public_url: body.public_url || null,
            manage_url: body.manage_url || null,
          },
        });
      })
      .catch(() => {
        this.setState({
          submitting: false,
          error: "Couldn't publish your badge. Please try again.",
        });
      });
  };

  closeConfirmation = () => {
    this.setState({ consentResult: null, copied: false });
  };

  copyUrl = () => {
    const url = this.state.consentResult && this.state.consentResult.public_url;
    if (!url) return;
    const flash = () => {
      this.setState({ copied: true });
      if (this._copyTimeout) clearTimeout(this._copyTimeout);
      this._copyTimeout = setTimeout(
        () => this.setState({ copied: false }),
        2000
      );
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(flash).catch(() => {});
      return;
    }
    // Legacy fallback for older browsers without navigator.clipboard.
    try {
      const el = document.createElement("textarea");
      el.value = url;
      el.style.position = "fixed";
      el.style.opacity = "0";
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      flash();
    } catch (e) { /* swallow */ }
  };

  decline = () => {
    const { badge, submitting } = this.state;
    if (!badge || submitting) return;
    this.setState({ submitting: true, error: null });
    const apiUrl = process.env.REACT_APP_API_URL;
    postRequest(`${apiUrl}v2/badges/decline/${badge.consent_token}/`)
      .send({})
      .then(() => {
        this.setState({
          submitting: false,
          tourOpen: false,
          designOpen: false,
          badge: Object.assign({}, badge, { is_declined: true }),
        });
      })
      .catch(() => {
        this.setState({
          submitting: false,
          error: "Couldn't decline your badge. Please try again.",
        });
      });
  };

  // Replace inline "LinkedIn" / "Handshake" mentions with external
  // link elements. Returns an array of strings and elements suitable
  // for React 15 children (no Fragment needed).
  renderInlineLinks = (text, keyPrefix) => {
    const LINK_MAP = {
      LinkedIn: "https://www.linkedin.com/",
      Handshake: "https://joinhandshake.com/",
    };
    const regex = /\b(LinkedIn|Handshake)\b/g;
    const out = [];
    let lastIndex = 0;
    let match;
    let n = 0;
    // eslint-disable-next-line no-cond-assign
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        out.push(text.substring(lastIndex, match.index));
      }
      out.push(
        <TourExternalLink
          key={`${keyPrefix}-lnk-${n}`}
          href={LINK_MAP[match[1]]}
          target="_blank"
          rel="noopener noreferrer"
        >
          {match[1]}
        </TourExternalLink>
      );
      lastIndex = match.index + match[0].length;
      n += 1;
    }
    if (lastIndex < text.length) {
      out.push(text.substring(lastIndex));
    }
    return out;
  };

  // Renders the tour message body as a series of paragraphs. Three
  // special paragraphs get bespoke treatment:
  //   - "Here's your badge:" is replaced with a "View your badge"
  //     button that opens the badge design popup. The consent URL
  //     itself is stripped out first.
  //   - The résumé-wording paragraph is wrapped in a highlight box so
  //     the student can spot the copy-paste block at a glance.
  //   - All other paragraphs render with inline LinkedIn / Handshake
  //     mentions turned into external links.
  // Falls back to the raw stripped message string if the message can't
  // be split into paragraphs (defensive against instructor-customized
  // copy that drops the double-newline structure).
  renderTourBody = (message) => {
    const stripped = message.replace(
      /\s*https?:\/\/badges\.marketgames\.io\/consent\/[^\s]+/g,
      ""
    );
    const paragraphs = stripped
      .split(/\n\n+/)
      .map(p => p.trim())
      .filter(p => p.length > 0);

    if (paragraphs.length === 0) return stripped;

    return paragraphs.map((p, i) => {
      const key = `p-${i}`;

      if (/Here's your badge:/i.test(p)) {
        return (
          <TourParagraph key={key}>
            <ViewBadgeLink
              type="button"
              onClick={this.openDesign}
            >
              View your badge
            </ViewBadgeLink>
          </TourParagraph>
        );
      }

      if (/^Market Games Skills Badge, awarded to the top/i.test(p)) {
        return (
          <TourHighlightBox key={key}>
            {this.renderInlineLinks(p, key)}
          </TourHighlightBox>
        );
      }

      return (
        <TourParagraph key={key}>
          {this.renderInlineLinks(p, key)}
        </TourParagraph>
      );
    });
  };

  render() {
    const {
      badge,
      tourOpen,
      designOpen,
      submitting,
      error,
      insightsData,
      insightsLoading,
      insightsError,
      consentResult,
      copied,
    } = this.state;

    // Confirmation screen — shown after a successful Accept. Checked
    // BEFORE the terminal-state guard because badge.is_consented is
    // also true at this point (patched in the approve success handler),
    // and we still want to render the confirmation until the student
    // explicitly dismisses it.
    if (consentResult) {
      const publicUrl = consentResult.public_url;
      return (
        <Backdrop>
          <ConfirmationCard onClick={e => e.stopPropagation()}>
            <ConfirmationCheckmark aria-hidden>✓</ConfirmationCheckmark>
            <ConfirmationTitle>Skills Badge Accepted!</ConfirmationTitle>
            <ConfirmationDescription>
              Your Skills Badge is now live. We've also sent you the
              link by email so you can share it anytime.
            </ConfirmationDescription>
            {publicUrl && (
              <div>
                <UrlLabel>Your public badge URL</UrlLabel>
                <UrlRow>
                  <UrlField
                    type="text"
                    value={publicUrl}
                    readOnly
                    onFocus={e => e.target.select()}
                  />
                  <CopyButton
                    type="button"
                    onClick={this.copyUrl}
                  >
                    {copied ? "Copied!" : "Copy"}
                  </CopyButton>
                </UrlRow>
              </div>
            )}
            <ConfirmationActions>
              <SecondaryButton
                type="button"
                onClick={this.closeConfirmation}
              >
                Done
              </SecondaryButton>
              {publicUrl && (
                <ViewButton
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Badge
                </ViewButton>
              )}
            </ConfirmationActions>
          </ConfirmationCard>
        </Backdrop>
      );
    }

    // Nothing to prompt if no pending badge OR the badge is in a
    // terminal state (guards against stale state after Approve /
    // Decline while a race condition could re-render).
    if (!badge) return null;
    if (badge.is_consented || badge.is_declined) return null;

    // Design popup takes highest priority — replaces the tour view
    // once opened. Renders the shared SkillsBadgePopup with the
    // student action buttons (Decline / Accept) in its footer slot.
    if (designOpen) {
      // React 15 has no Fragment — pass footer children as a keyed array.
      const badgeFooter = [
        error && <ErrorMsg key="err">{error}</ErrorMsg>,
        <DeclineButton
          key="dc"
          type="button"
          onClick={this.decline}
          disabled={submitting}
        >
          {submitting ? "Working…" : "Decline, the Badge"}
        </DeclineButton>,
        <AcceptButtonWrap key="ac">
          <PrimaryButton
            type="button"
            onClick={this.approve}
            disabled={submitting}
          >
            {submitting ? "Working…" : "Accept the Skills Badge"}
          </PrimaryButton>
          <div className="accept-tooltip">
            Once accepted, you'll receive an email with your Skills Badge
            link. Add it to LinkedIn or your résumé to help you stand out
            for internships and jobs.
          </div>
        </AcceptButtonWrap>,
      ];
      // Merge the badge.snapshot fields into the insights payload so
      // SkillsBadgePopup can read snapshot-only fields like
      // instructor_last_name / course_institution. Insights fields
      // take precedence when both sources have the same key.
      const snapshot = (badge && badge.snapshot) || {};
      const mergedData = Object.assign({}, snapshot, insightsData || {});
      // Fallback for instructor_last_name + course_institution:
      // general-info?position=student already ships data.user
      // .course_instructor_last_name and .institution (persisted via
      // Auth.setUserData). Use those when neither the badge snapshot
      // nor /insights/mine/ populated the fields — no extra API call,
      // just a local Auth read.
      const authUser = Auth.getUser() || {};
      if (!mergedData.instructor_last_name && authUser.course_instructor_last_name) {
        mergedData.instructor_last_name = authUser.course_instructor_last_name;
      }
      if (!mergedData.course_institution && authUser.institution) {
        mergedData.course_institution = authUser.institution;
      }
      return (
        <SkillsBadgePopup
          open
          data={mergedData}
          loading={insightsLoading}
          error={insightsError}
          studentName={this.getStudentName()}
          teammateCount={this.getTeammateCount()}
          footer={badgeFooter}
        />
      );
    }

    // Tour: congratulations text with "View your badge" link inline.
    // Only exit is Continue (opens the design popup).
    if (tourOpen) {
      const message = badge.student_message
        || "Congratulations on earning the Market Games Skills Badge!";
      return (
        <Backdrop>
          <TourCard onClick={e => e.stopPropagation()}>
            <TourHeader>
              <TourTitle>Market Games Skills Badge</TourTitle>
            </TourHeader>
            <TourBody>{this.renderTourBody(message)}</TourBody>
            <TourFooter>
              <PrimaryButton type="button" onClick={this.openDesign}>
                View Badge
              </PrimaryButton>
            </TourFooter>
          </TourCard>
        </Backdrop>
      );
    }

    // Intro popup — no dismiss affordance; the student must click
    // Continue to move forward. The badge stays pending on the BE
    // until Accept / Decline, so the flow re-appears on re-login.
    return (
      <Backdrop>
        <IntroCard onClick={e => e.stopPropagation()}>
          <IntroTitle>
            Congratulations! You've earned the Market Games Skills Badge!
          </IntroTitle>
          <IntroActions>
            <PrimaryButton type="button" onClick={this.openTour}>
              Continue
            </PrimaryButton>
          </IntroActions>
        </IntroCard>
      </Backdrop>
    );
  }
}

export default StudentBadgesFlow;



// WEBPACK FOOTER //
// ./src/components/student-badges/StudentBadgesFlow.js